import React, { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { exportSquareCover, squareCropRect } from './coverCropModel';
export default function PlaylistCoverCrop({ file, en, onCancel, onUse }) {
  const [source, setSource] = useState(''),
    [image, setImage] = useState(null);
  const [position, setPosition] = useState([0.5, 0.5, 1]);
  const [error, setError] = useState(false),
    [busy, setBusy] = useState(false);
  const alive = useRef(false),
    lock = useRef(false),
    drag = useRef(null);
  const id = React.useId();
  useEffect(() => {
    alive.current = true;
    const url = URL.createObjectURL(file);
    setSource(url);
    return () => {
      alive.current = false;
      URL.revokeObjectURL(url);
    };
  }, [file]);
  const useImage = async () => {
    if (!image || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(false);
    try {
      const result = await exportSquareCover(image, position);
      if (alive.current) onUse(result);
    } catch {
      if (alive.current) setError(true);
    } finally {
      if (alive.current) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const rect = image && squareCropRect(image.naturalWidth, image.naturalHeight, ...position);
  const pan = (e) => {
    if (!drag.current || !rect || busy) return;
    const { startX, startY, position: start, width } = drag.current;
    const size = Math.min(image.naturalWidth, image.naturalHeight) / start[2];
    const move = (delta, range, value) =>
      range > 0 ? Math.max(0, Math.min(1, value - (delta * size) / width / range)) : value;
    setPosition([
      move(e.clientX - startX, image.naturalWidth - size, start[0]),
      move(e.clientY - startY, image.naturalHeight - size, start[1]),
      start[2],
    ]);
  };
  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      className="pp-review-dialog pp-editor-crop-dialog"
      aria-labelledby={id}
      onClose={() => {
        if (!lock.current) onCancel();
      }}
    >
      <DialogTitle id={id}>{en ? 'Crop cover image' : '대표 이미지 자르기'}</DialogTitle>
      <DialogContent>
        <div
          className="pp-editor-crop-stage"
          onPointerDown={(e) => {
            if (!image || busy || e.button !== 0) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            drag.current = {
              startX: e.clientX,
              startY: e.clientY,
              position: [...position],
              width: e.currentTarget.getBoundingClientRect().width,
            };
          }}
          onPointerMove={pan}
          onPointerUp={() => {
            drag.current = null;
          }}
          onPointerCancel={() => {
            drag.current = null;
          }}
        >
          {source && (
            <img
              src={source}
              alt={en ? 'Cover crop preview' : '커버 자르기 미리보기'}
              draggable={false}
              onLoad={(e) => setImage(e.currentTarget)}
              onError={() => setError(true)}
              style={
                rect
                  ? {
                      width: `${(image.naturalWidth / rect.size) * 100}%`,
                      height: `${(image.naturalHeight / rect.size) * 100}%`,
                      left: `${(-rect.x / rect.size) * 100}%`,
                      top: `${(-rect.y / rect.size) * 100}%`,
                    }
                  : { visibility: 'hidden' }
              }
            />
          )}
        </div>
        <p>
          {en
            ? 'Drag the image or use the controls to adjust the square crop.'
            : '이미지를 움직이거나 아래 조절바로 정사각형 영역을 맞춰주세요.'}
        </p>
        {[
          en ? 'Horizontal position' : '가로 위치',
          en ? 'Vertical position' : '세로 위치',
          en ? 'Zoom' : '확대',
        ].map((label, index) => (
          <label className="pp-editor-crop-control" key={label}>
            {label}
            <input
              type="range"
              min={index === 2 ? 1 : 0}
              max={index === 2 ? 3 : 1}
              step="0.01"
              value={position[index]}
              disabled={!image || busy}
              onChange={(e) => {
                const value = Number(e.target.value);
                setPosition((p) => p.map((v, i) => (i === index ? value : v)));
              }}
            />
          </label>
        ))}
        {error && (
          <p role="alert">
            {en
              ? 'Could not prepare this image. Try again or choose another photo.'
              : '이미지를 준비하지 못했어요. 다시 시도하거나 다른 사진을 선택해 주세요.'}
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onCancel}>
          {en ? 'Cancel' : '취소'}
        </Button>
        <Button disabled={!image || busy} onClick={useImage}>
          {busy ? (en ? 'Preparing…' : '준비 중…') : en ? 'Use photo' : '사진 사용'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
